import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, ImagePlus, Loader2 } from "lucide-react";
import { assetUrl, fmtDate, timeRange, todayStr } from "@/lib/events";
import { toast } from "sonner";

const inp = "mt-1 w-full h-10 px-3 rounded-lg border border-[#d6cecf]";
const EMPTY = { id: null, title: "", date: "", start_time: "", end_time: "", location_name: "", address: "", city: "", description: "", register_url: "", price: "", class_id: "" };
const FIELDS = ["title", "date", "start_time", "end_time", "location_name", "address", "city", "description", "register_url"];

export function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showPast, setShowPast] = useState(false);
  const [classes, setClasses] = useState([]);
  const [uploading, setUploading] = useState(null);
  const fileRef = useRef(null);
  const target = useRef(null);

  const load = () => api.get("/events").then(({ data }) => setEvents(data.slice().reverse())).catch(() => {});
  useEffect(() => { load(); api.get("/admin/classes").then(({ data }) => setClasses(data)).catch(() => {}); }, []);
  const field = (k, v) => setEditing((e) => ({ ...e, [k]: v }));
  const shown = showPast ? events : events.filter((e) => e.date >= todayStr());

  const save = async () => {
    const body = Object.fromEntries(FIELDS.map((k) => [k, editing[k] || ""]));
    const price = String(editing.price ?? "").trim();
    if (price && !(Number(price) >= 0)) { toast.error("Price must be a number, e.g. 28 or 27.50"); return; }
    body.price_cents = price ? Math.round(Number(price) * 100) : null;
    body.class_id = editing.class_id || "";
    if (!body.title.trim() || !body.date) { toast.error("Title and date are required"); return; }
    try {
      if (editing.id) await api.put(`/admin/events/${editing.id}`, body);
      else await api.post("/admin/events", body);
      toast.success("Event saved"); setEditing(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (e) => {
    if (!window.confirm(`Delete "${e.title}" on ${e.date}?`)) return;
    await api.delete(`/admin/events/${e.id}`); load();
  };
  const pick = (e) => { target.current = e; fileRef.current.click(); };
  const onFile = async (ev) => {
    const f = ev.target.files?.[0]; ev.target.value = "";
    if (!f) return;
    const e = target.current; setUploading(e.id);
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/admin/events/${e.id}/image`, fd); toast.success("Flyer uploaded"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(null); }
  };

  return (
    <div data-testid="admin-events">
      <div className="flex flex-wrap items-center justify-between mb-4 gap-3">
        <label className="flex items-center gap-2 text-sm text-[#545454]">
          <input type="checkbox" checked={showPast} onChange={(e) => setShowPast(e.target.checked)} data-testid="events-show-past" className="accent-[#920e12]" /> Show past events
        </label>
        <Button onClick={() => setEditing({ ...EMPTY })} className="bg-[#920e12] hover:bg-[#550001] text-white" data-testid="event-add-btn"><Plus size={16} className="mr-1" /> Add event</Button>
      </div>
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={onFile} data-testid="event-image-input" />
      <div className="space-y-2">
        {shown.map((e) => (
          <div key={e.id} className="flex items-center gap-3 bg-white rounded-xl border border-[#dfd8da] p-3" data-testid={`admin-event-${e.id}`}>
            {e.image_url ? <img src={assetUrl(e.image_url)} alt="" className="w-16 h-12 object-cover rounded" /> : <div className="w-16 h-12 rounded bg-[#f9f6f6]" />}
            <div className="flex-1 min-w-0">
              <div className="font-bold text-[#1a1617] truncate">{e.title}</div>
              <div className="text-xs text-[#545454]">{fmtDate(e.date, { month: "short", day: "numeric", year: "numeric" })}{timeRange(e) && ` · ${timeRange(e)}`}{e.location_name ? ` · ${e.location_name}` : ""}{e.price_cents ? ` · Sign up $${(e.price_cents / 100).toFixed(2)}` : ""}</div>
            </div>
            <Button size="sm" variant="outline" onClick={() => pick(e)} disabled={uploading === e.id} title="Upload flyer" data-testid={`event-upload-${e.id}`} className="border-[#920e12] text-[#920e12]">
              {uploading === e.id ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}</Button>
            <Button size="sm" variant="outline" onClick={() => setEditing({ ...e, price: e.price_cents ? (e.price_cents / 100).toString() : "" })} data-testid={`event-edit-${e.id}`} className="border-[#920e12] text-[#920e12]"><Pencil size={14} /></Button>
            <Button size="sm" variant="outline" onClick={() => remove(e)} data-testid={`event-delete-${e.id}`} className="border-[#991b1b] text-[#991b1b]"><Trash2 size={14} /></Button>
          </div>
        ))}
        {shown.length === 0 && <p className="text-[#545454]" data-testid="admin-events-empty">No upcoming events. Add one, or tick "Show past events".</p>}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="event-editor">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit event" : "New event"}</DialogTitle>
            <DialogDescription>Times are Eastern. Add the flyer from the list after saving.</DialogDescription></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label htmlFor="ev-title">Title</Label><input id="ev-title" value={editing.title} onChange={(e) => field("title", e.target.value)} className={inp} data-testid="event-title" /></div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><Label htmlFor="ev-date">Date</Label><input id="ev-date" type="date" value={editing.date} onChange={(e) => field("date", e.target.value)} className={inp} data-testid="event-date" /></div>
                <div><Label htmlFor="ev-start">Start</Label><input id="ev-start" type="time" value={editing.start_time} onChange={(e) => field("start_time", e.target.value)} className={inp} data-testid="event-start" /></div>
                <div><Label htmlFor="ev-end">End</Label><input id="ev-end" type="time" value={editing.end_time} onChange={(e) => field("end_time", e.target.value)} className={inp} data-testid="event-end" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label htmlFor="ev-loc">Venue</Label><input id="ev-loc" value={editing.location_name} onChange={(e) => field("location_name", e.target.value)} className={inp} data-testid="event-location" /></div>
                <div><Label htmlFor="ev-city">City</Label><input id="ev-city" value={editing.city} onChange={(e) => field("city", e.target.value)} className={inp} data-testid="event-city" /></div>
              </div>
              <div><Label htmlFor="ev-addr">Address</Label><input id="ev-addr" value={editing.address} onChange={(e) => field("address", e.target.value)} className={inp} data-testid="event-address" /></div>
              <div><Label htmlFor="ev-desc">Description</Label><textarea id="ev-desc" rows={4} value={editing.description} onChange={(e) => field("description", e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#d6cecf]" data-testid="event-description" /></div>
              <div><Label htmlFor="ev-reg">Registration link (optional)</Label><input id="ev-reg" value={editing.register_url} onChange={(e) => field("register_url", e.target.value)} placeholder="https://…" className={inp} data-testid="event-register-url" /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-[#f9f6f6] rounded-xl p-3 border border-[#dfd8da]">
                <div><Label htmlFor="ev-price">Sign-up price in $ (optional)</Label><input id="ev-price" inputMode="decimal" value={editing.price} onChange={(e) => field("price", e.target.value)} placeholder="e.g. 28" className={`${inp} bg-white`} data-testid="event-price" />
                  <p className="mt-1 text-xs text-[#545454]">Adds a "Sign up" button with card checkout. Leave blank for no online sign-up.</p></div>
                <div><Label htmlFor="ev-class">Add paid sign-ups to class roster</Label>
                  <select id="ev-class" value={editing.class_id} onChange={(e) => field("class_id", e.target.value)} className={`${inp} bg-white`} data-testid="event-class">
                    <option value="">— Don't add to a roster —</option>
                    {classes.map((c) => <option key={c.id} value={c.id}>{`L${c.level} · S${c.session_no} · C${c.class_no}${c.title ? ` — ${c.title}` : ""}`}</option>)}
                  </select></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} className="bg-[#920e12] text-white" data-testid="event-save">Save event</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
